/* eslint-disable react-native/no-color-literals */
import React, { useState } from "react";
import {
  Modal,
  Pressable,
  StyleSheet,
  TouchableOpacity,
  View,
  FlatList,
  ActivityIndicator,
  Image,
} from "react-native";
import {
  SafeAreaProvider,
  useSafeAreaInsets,
} from "react-native-safe-area-context";
import Checkbox from "@components/Checkbox";
import Text from "@components/CustomText";
import Cross from "@assets/svg/CrossIcon";
import SkeletonLoaderList from "@components/SkeletonLoaderList";
import colors from "@constants/color";
import CustomPicker from "@components/CustomDropdownPicker/Picker";

const ItemSeparator = () => <View style={separatorStyle} />;
const separatorStyle = {
  height: StyleSheet.hairlineWidth,
  backgroundColor: colors.black02,
  marginLeft: 84,
};

interface PickupItem {
  order_item_id: number;
  order_id: number;
  title: string;
  product_image?: string;
  expected_quantity: number;
  selected_quantity: number;
  is_selected: boolean;
}

interface Props {
  visible: boolean;
  items: PickupItem[];
  isLoading: boolean;
  isSubmitting: boolean;
  onClose: () => void;
  onSubmit: () => void;
  onQuantityChange: (orderItemId: number, nextQuantity: number) => void;
  onSelectionChange: (orderItemId: number, isSelected: boolean) => void;
}

const PickupConfirmationModalContent: React.FC<Props> = ({
  items,
  isLoading,
  isSubmitting,
  onClose,
  onSubmit,
  onQuantityChange,
  onSelectionChange,
}) => {
  const [openDropdownFor, setOpenDropdownFor] = useState<number | null>(null);
  // Insets from the SafeAreaProvider nested inside the Modal (wrapper below),
  // so the value reflects the modal's own translucent window. The root
  // provider reports bottom: 0 while a navigationBarTranslucent modal is open,
  // clipping the footer behind the soft-nav bar.
  const insets = useSafeAreaInsets();

  const renderItem = ({ item }: { item: PickupItem }) => (
    <View style={styles.row}>
      <TouchableOpacity
        style={styles.rowLeft}
        onPress={() => onSelectionChange(item.order_item_id, !item.is_selected)}
        activeOpacity={0.7}
      >
        {item.product_image ? (
          <Image
            source={{ uri: item.product_image }}
            style={styles.productImage}
            resizeMode="cover"
          />
        ) : (
          <View style={[styles.productImage, styles.productImagePlaceholder]} />
        )}
        <View style={styles.itemInfo}>
          <Text
            variant="body_text_4"
            style={styles.itemTitle}
            numberOfLines={2}
          >
            {item.title}
          </Text>
          <Text variant="caption_2" style={styles.orderLabel}>
            Order #{item.order_id}
          </Text>
        </View>
      </TouchableOpacity>

      <View style={styles.controls}>
        <View
          style={[
            styles.qtyPickerWrapper,
            !item.is_selected && styles.qtyDisabled,
          ]}
        >
          <CustomPicker
            items={Array.from(
              { length: item.expected_quantity + 1 },
              (_, qty) => ({ label: `${qty}`, value: qty }),
            )}
            value={item.selected_quantity}
            setValue={(value: number) =>
              onQuantityChange(item.order_item_id, value)
            }
            open={openDropdownFor === item.order_item_id}
            setOpen={(open: boolean) =>
              setOpenDropdownFor(open ? item.order_item_id : null)
            }
            placeholder="Qty"
            style={styles.dropdown}
            textStyle={styles.dropdownText}
          />
          <Text variant="caption_2" style={styles.totalQtyLabel}>
            total: {item.expected_quantity}
          </Text>
        </View>
        <Checkbox
          value={item.is_selected}
          onValueChange={(v) => onSelectionChange(item.order_item_id, v)}
          tintColors={{ true: colors.plain_green }}
        />
      </View>
    </View>
  );

  const pickedUnits = items.reduce(
    (sum, i) => sum + (i.is_selected ? i.selected_quantity : 0),
    0,
  );
  const totalUnits = items.reduce((sum, i) => sum + i.expected_quantity, 0);

  return (
    <>
      <View style={styles.overlay} />

      <View style={[styles.sheet, { paddingBottom: insets.bottom }]}>
        <Pressable style={styles.closeBtn} onPress={onClose}>
          <Cross width={14} height={14} />
        </Pressable>

        <View style={styles.header}>
          <Text variant="title_20">Confirm picked items</Text>
          {!isLoading && items.length > 0 && (
            <Text variant="caption_2" style={styles.pickedCount}>
              {pickedUnits}/{totalUnits} units picked
            </Text>
          )}
        </View>

        <View style={styles.divider} />

        {isLoading ? (
          <View style={styles.skeletonWrapper}>
            <SkeletonLoaderList isLoading length={5} />
          </View>
        ) : (
          <FlatList
            data={items}
            keyExtractor={(item) => `${item.order_item_id}`}
            renderItem={renderItem}
            showsVerticalScrollIndicator={false}
            ItemSeparatorComponent={ItemSeparator}
            ListEmptyComponent={
              <View style={styles.emptyState}>
                <Text variant="body_text_3" style={styles.emptyText}>
                  No items found
                </Text>
              </View>
            }
            contentContainerStyle={styles.listContent}
          />
        )}

        <View style={styles.footer}>
          <TouchableOpacity
            style={[styles.footerBtn, styles.cancelBtn]}
            onPress={onClose}
            disabled={isSubmitting}
            activeOpacity={0.7}
          >
            <Text variant="body_text_2" style={styles.cancelText}>
              Cancel
            </Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[
              styles.footerBtn,
              styles.submitBtn,
              (isLoading || items.length === 0) && styles.submitBtnDisabled,
            ]}
            onPress={onSubmit}
            disabled={isLoading || isSubmitting || items.length === 0}
            activeOpacity={0.7}
          >
            {isSubmitting ? (
              <ActivityIndicator size="small" color={colors.white} />
            ) : (
              <Text variant="body_text_2" style={styles.submitText}>
                Submit
              </Text>
            )}
          </TouchableOpacity>
        </View>
      </View>
    </>
  );
};

const PickupConfirmationModal: React.FC<Props> = (props) => {
  return (
    <Modal
      animationType="slide"
      transparent={true}
      visible={props.visible}
      onRequestClose={() => {}}
      statusBarTranslucent
      navigationBarTranslucent
    >
      {/* Nested provider measures the modal's own translucent window so
          useSafeAreaInsets() reports the real nav-bar inset inside the Modal. */}
      <SafeAreaProvider>
        <PickupConfirmationModalContent {...props} />
      </SafeAreaProvider>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: "rgba(0,0,0,0.4)",
  },
  sheet: {
    position: "absolute",
    bottom: 0,
    width: "100%",
    backgroundColor: colors.white,
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    maxHeight: "75%",
  },
  closeBtn: {
    position: "absolute",
    right: 10,
    top: -34,
    backgroundColor: colors.white,
    borderRadius: 50,
    padding: 8,
    zIndex: 1,
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingHorizontal: 16,
    paddingTop: 20,
    paddingBottom: 12,
  },
  pickedCount: {
    color: colors.plain_green,
  },
  divider: {
    height: StyleSheet.hairlineWidth,
    backgroundColor: colors.black02,
  },
  skeletonWrapper: {
    padding: 16,
  },
  listContent: {
    paddingVertical: 4,
  },
  row: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  rowLeft: {
    flex: 1,
    flexDirection: "row",
    alignItems: "center",
  },
  productImage: {
    width: 56,
    height: 56,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: colors.lighterBlack,
  },
  productImagePlaceholder: {
    backgroundColor: colors.primaryBg,
  },
  itemInfo: {
    flex: 1,
    marginHorizontal: 12,
  },
  itemTitle: {
    color: colors.mediumBlack,
    marginBottom: 4,
  },
  orderLabel: {
    color: colors.mediumBlacklightBlack,
  },
  controls: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  qtyPickerWrapper: {
    width: 72,
  },
  qtyDisabled: {
    opacity: 0.35,
  },
  totalQtyLabel: {
    color: colors.mediumBlacklightBlack,
    textAlign: "center",
    marginTop: 3,
  },
  dropdown: {
    minHeight: 36,
    borderColor: colors.lightMediumBlack,
    borderRadius: 6,
  },
  dropdownText: {
    color: colors.black,
    fontSize: 13,
  },
  footer: {
    flexDirection: "row",
    gap: 10,
    padding: 16,
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: colors.black02,
  },
  footerBtn: {
    flex: 1,
    height: 44,
    borderRadius: 8,
    alignItems: "center",
    justifyContent: "center",
  },
  cancelBtn: {
    borderWidth: 1,
    borderColor: colors.lightMediumBlack,
    backgroundColor: colors.white,
  },
  submitBtn: {
    backgroundColor: colors.plain_green,
  },
  submitBtnDisabled: {
    backgroundColor: colors.silver,
  },
  cancelText: {
    color: colors.black,
  },
  submitText: {
    color: colors.white,
  },
  emptyState: {
    paddingVertical: 32,
    alignItems: "center",
  },
  emptyText: {
    color: colors.mediumBlacklightBlack,
  },
});

export default PickupConfirmationModal;
